<template>
  <div class="login-page">
    <div class="login-card">
      <!-- Header -->
      <div class="login-header">
        <img
          src="@/assets/images/logot.svg"
          class="login-logo-m"
          alt="Decast"
        />
        <h1>Welcome back</h1>
        <p class="login-subtitle">Sign in to your Decast account</p>
      </div>

      <!-- DecastID - hero/recommended -->
      <div class="did-hero">
        <DidsLogin />
        <a class="did-learn" @click="$router.push('/what-is-did')">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <circle
              cx="8"
              cy="8"
              r="7"
              stroke="currentColor"
              stroke-width="1.3"
            />
            <path
              d="M8 7v4M8 5.5h.01"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
            />
          </svg>
          What's a DecastID?
        </a>
      </div>

      <!-- Divider -->
      <div class="sep">
        <hr />
        <span>or</span>
        <hr />
      </div>

      <!-- Other social logins -->
      <div class="social-stack">
        <GoogleLogin />
        <MetamaskLogin />
      </div>

      <!-- Email link -->
      <a class="email-link" @click="showEmailModal = true"
        >Sign in with email instead</a
      >

      <!-- Footer -->
      <p class="login-footer">
        Don't have an account?&nbsp;<a @click="navigateToSignUp">Sign Up</a>
      </p>
    </div>

    <!-- Email/Password Modal -->
    <transition name="fade">
      <div
        class="modal-overlay"
        v-if="showEmailModal"
        @click.self="closeEmailModal"
      >
        <div class="modal-card">
          <div class="modal-head">
            <h2>Sign in with email</h2>
            <button class="modal-close" @click="closeEmailModal" type="button">
              &times;
            </button>
          </div>

          <div v-if="formLevel === 1">
            <div class="field">
              <label>Email address</label>
              <input
                type="email"
                v-model="email"
                placeholder="name@example.com"
                @keyup.enter="handleSubmit"
              />
              <p v-if="emailError" class="field-error">{{ emailError }}</p>
            </div>

            <div class="field">
              <div class="field-top">
                <label>Password</label>
                <a class="forgot-link" @click="updateFormLevel(2)">Forgot?</a>
              </div>
              <div class="input-icon-wrap">
                <input
                  :type="passwordInputType"
                  v-model="password"
                  placeholder="Enter password"
                  @keyup.enter="handleSubmit"
                />
                <button
                  class="eye-btn"
                  @click="togglePasswordVisibility"
                  type="button"
                  tabindex="-1"
                >
                  <EyeVisible v-if="isPasswordVisible" />
                  <EyeHide v-else />
                </button>
              </div>
              <p v-if="passwordError" class="field-error">
                {{ passwordError }}
              </p>
            </div>

            <label class="checkbox-row">
              <input type="checkbox" v-model="checkbox_remember_me" />
              <span>Remember me</span>
            </label>

            <button class="btn-primary" @click="handleSubmit">Sign In</button>
          </div>

          <div v-else-if="formLevel === 2">
            <p class="form-desc">
              Enter your email and we'll send you a reset link.
            </p>
            <div class="field">
              <label>Email address</label>
              <input
                type="email"
                v-model="email"
                placeholder="name@example.com"
              />
            </div>
            <button class="btn-primary" @click="sendForgetPasswordLink">
              Send Reset Link
            </button>
            <button class="btn-ghost" @click="updateFormLevel(1)">
              Back to sign in
            </button>
          </div>

          <div v-else-if="formLevel === 3">
            <div class="notice">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="8"
                  stroke="#D7DF23"
                  stroke-width="1.5"
                />
                <path
                  d="M9 5.5v4M9 12h.01"
                  stroke="#D7DF23"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              <span>Check your email for the reset link.</span>
            </div>
            <button class="btn-ghost" @click="updateFormLevel(1)">
              Back to sign in
            </button>
          </div>

          <div v-else-if="formLevel === 4">
            <div class="notice">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="8"
                  stroke="#D7DF23"
                  stroke-width="1.5"
                />
                <path
                  d="M9 5.5v4M9 12h.01"
                  stroke="#D7DF23"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              <span>Check your email for the magic link.</span>
            </div>
            <button class="btn-ghost" @click="updateFormLevel(1)">
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { afterAuthPath } from '@/http/requests/billing';
import EyeHide from '@/decast/icons/EyeHide.vue';
import EyeVisible from '@/decast/icons/EyeVisible.vue';
import GoogleLogin from '@/decast/components/v2auth/social-logins/GoogleLogin.vue';
import MetamaskLogin from '@/decast/components/v2auth/social-logins/MetamaskLogin.vue';
import DidsLogin from '@/decast/components/v2auth/social-logins/DidsLogin.vue';

export default {
  data() {
    return {
      email: '',
      password: '',
      formLevel: 1,
      emailError: '',
      passwordError: '',
      checkbox_remember_me: false,
      isPasswordVisible: false,
      showEmailModal: false,
    };
  },
  components: {
    EyeHide,
    EyeVisible,
    GoogleLogin,
    MetamaskLogin,
    DidsLogin,
  },
  mounted() {
    document.getElementById('loading-bg-transparent').style.display = 'none';
  },
  computed: {
    passwordInputType() {
      return this.isPasswordVisible ? 'text' : 'password';
    },
  },
  methods: {
    updateFormLevel(formLevel) {
      this.formLevel = formLevel;
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
    navigateToSignUp() {
      this.$router.push('/v2signup');
    },
    closeEmailModal() {
      this.showEmailModal = false;
      this.formLevel = 1;
      this.emailError = '';
      this.passwordError = '';
    },
    handleSubmit() {
      if (!this.email) {
        this.emailError = 'Email is required.';
        return;
      }
      if (!this.password) {
        this.passwordError = 'Password is required.';
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.emailError = 'Enter a valid email address.';
        return;
      }
      this.emailError = '';
      this.passwordError = '';
      this.loginWithJWT();
    },
    handleSendMagicLink() {
      document.getElementById('loading-bg-transparent-title').innerText =
        'Sending magic link to your email';
      document.getElementById('loading-bg-transparent').style.display = 'flex';
      const payload = { email: this.email };
      this.$store
        .dispatch('auth/v2sendMagicLink', payload)
        .then((response) => {
          if (response?.status === 204) {
            this.$vs.notify({
              title: 'Successful',
              text: 'Magic link has been sent to your email address',
              iconPack: 'feather',
              icon: 'icon-check-circle',
              color: 'success',
            });
            this.updateFormLevel(4);
          }
        })
        .catch((error) => {
          const errorResponse = error.response?.data;
          let message = 'An error occurred.';
          if (errorResponse?.errors?.email) {
            message = 'Account does not exist!';
          } else if (errorResponse?.errors?.password) {
            message = 'Incorrect password, Please try again!';
          } else if (typeof errorResponse?.errors === 'string') {
            message = errorResponse.errors;
          }
          this.$vs.notify({
            title: 'Failed to send magic link',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
          });
        })
        .finally(() => {
          document.getElementById('loading-bg-transparent').style.display =
            'none';
        });
    },
    sendForgetPasswordLink() {
      document.getElementById('loading-bg-transparent-title').innerText =
        'Sending reset password link to email';
      document.getElementById('loading-bg-transparent').style.display = 'flex';
      const payload = { email: this.email };
      this.$store
        .dispatch('auth/v2sendForgetPasswordLink', payload)
        .then((response) => {
          if (response?.status === 204) {
            this.$vs.notify({
              title: 'Reset link send',
              text: 'Email has been sent to reset your password',
              iconPack: 'feather',
              icon: 'icon-check-circle',
              color: 'success',
            });
            this.updateFormLevel(3);
          }
        })
        .catch((error) => {
          const errorResponse = error.response?.data;
          let message = 'An error occurred.';
          if (errorResponse?.errors?.email) {
            message = 'Account does not exist!';
          } else if (errorResponse?.errors?.password) {
            message = 'Incorrect password, Please try again!';
          } else if (typeof errorResponse?.errors === 'string') {
            message = errorResponse.errors;
          }
          this.$vs.notify({
            title: 'Failed to send reset link',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
          });
        })
        .finally(() => {
          document.getElementById('loading-bg-transparent').style.display =
            'none';
        });
    },
    loginWithJWT() {
      document.getElementById('loading-bg-transparent-title').innerText =
        'Logging to decast';
      document.getElementById('loading-bg-transparent').style.display = 'flex';
      const payload = {
        checkbox_remember_me: this.checkbox_remember_me,
        userDetails: {
          email: this.email,
          password: this.password,
        },
      };
      this.$store
        .dispatch('auth/v2loginUser', payload)
        .then((response) => {
          const isTwoFactorAuthEnabled = response.data?.twoFactorAuthentication;
          if (isTwoFactorAuthEnabled) {
            const twoFactorToken = response.data?.token;
            this.$router.push({
              name: 'TwoFactor',
              query: { tempToken: twoFactorToken },
            });
          } else {
            this.$store.commit('auth/SET_LOGGEDIN', true);
            this.$acl.change('user');
            this.$vs.notify({
              title: 'Login Successful',
              text: 'Redirecting to dashboard!',
              iconPack: 'feather',
              icon: 'icon-check-circle',
              color: 'success',
            });
            this.$router.push(afterAuthPath('/dashboard/rooms'));
          }
        })
        .catch((error) => {
          const errorResponse = error.response?.data;
          let message = 'An error occurred.';
          if (errorResponse?.errors?.email) {
            message = 'Account does not exist!';
          } else if (errorResponse?.errors?.password) {
            message = 'Incorrect password, Please try again!';
          } else if (typeof errorResponse?.errors === 'string') {
            message = errorResponse.errors;
          }
          this.$vs.notify({
            title: 'Login Failed',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
          });
        })
        .finally(() => {
          document.getElementById('loading-bg-transparent').style.display =
            'none';
        });
    },
  },
};
</script>

<style scoped>
*:not(i) {
  font-family: 'Rethink Sans', -apple-system, BlinkMacSystemFont, sans-serif !important;
}

/* ── Page ── */
.login-page {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  overflow-y: auto;
}

.login-card {
  width: 100%;
  max-width: 380px;
}

/* ── Header ── */
.login-header {
  margin-bottom: 28px;
}

.login-header h1 {
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  letter-spacing: -0.01em;
}

.login-subtitle {
  color: #6b6d7e;
  font-size: 14px;
  margin: 0;
  font-weight: 400;
}

/* ── Fields ── */
.field {
  margin-bottom: 16px;
}

.field label {
  font-size: 13px;
  font-weight: 500;
  color: #9b9db0;
  display: block;
  margin-bottom: 6px;
}

.field-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.field-top label {
  margin-bottom: 0;
}

.forgot-link {
  font-size: 12px;
  color: #6b6d7e;
  cursor: pointer;
  transition: color 0.15s;
}

.forgot-link:hover {
  color: #d7df23;
}

.field input[type='email'],
.field input[type='text'],
.field input[type='password'] {
  width: 100%;
  height: 42px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  padding: 0 14px;
  outline: none;
  transition: border-color 0.15s, background-color 0.15s;
}

.field input:hover {
  border-color: rgba(255, 255, 255, 0.14);
}

.field input:focus {
  border-color: rgba(215, 223, 35, 0.5);
  background: rgba(255, 255, 255, 0.06);
}

.field input::placeholder {
  color: #3e4050;
}

.input-icon-wrap {
  position: relative;
}

.input-icon-wrap input {
  padding-right: 42px !important;
}

.eye-btn {
  position: absolute;
  right: 0;
  top: 0;
  height: 42px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #4a4c5e;
  padding: 0;
  transition: color 0.15s;
}

.eye-btn:hover {
  color: #9b9db0;
}

.field-error {
  color: #f87171;
  font-size: 12px;
  margin-top: 5px;
}

/* ── Checkbox ── */
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #6b6d7e;
  margin-bottom: 20px;
}

.checkbox-row input[type='checkbox'] {
  width: 15px !important;
  height: 15px;
  accent-color: #d7df23;
  cursor: pointer;
}

/* ── Buttons ── */
.btn-primary {
  width: 100%;
  height: 42px;
  background: #d7df23;
  color: #0f1017;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.1s;
  font-family: 'Rethink Sans', sans-serif;
}

.btn-primary:hover {
  background: #c5cc1f;
}

.btn-primary:active {
  transform: scale(0.99);
}

.btn-ghost {
  width: 100%;
  height: 38px;
  background: none;
  color: #6b6d7e;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 6px;
  transition: color 0.15s;
  font-family: 'Rethink Sans', sans-serif;
}

.btn-ghost:hover {
  color: #d7df23;
}

/* ── Separator ── */
.sep {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0;
}

.sep hr {
  flex: 1;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 0;
}

.sep span {
  color: #4a4c5e;
  font-size: 12px;
  white-space: nowrap;
}

/* ── DecastID hero ── */
.did-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
}

.did-learn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #4a4c5e;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s;
}

.did-learn:hover {
  color: #d7df23;
}

.did-learn svg {
  flex-shrink: 0;
}

/* ── Email link ── */
.email-link {
  display: block;
  text-align: center;
  color: #4a4c5e;
  font-size: 12px;
  cursor: pointer;
  margin-top: 20px;
  transition: color 0.15s;
}

.email-link:hover {
  color: #9b9db0;
}

/* ── Social stack ── */
.social-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-card {
  width: 100%;
  max-width: 380px;
  background: #1b1d29;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 28px 24px;
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.modal-head h2 {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: #6b6d7e;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.15s;
}

.modal-close:hover {
  color: #ffffff;
}

/* ── Fade transition ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ── Notice ── */
.notice {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(215, 223, 35, 0.05);
  border: 1px solid rgba(215, 223, 35, 0.12);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.notice svg {
  flex-shrink: 0;
}

.notice span {
  color: #9b9db0;
  font-size: 13px;
  line-height: 1.4;
}

/* ── Form description ── */
.form-desc {
  color: #6b6d7e;
  font-size: 14px;
  margin-bottom: 18px;
  line-height: 1.5;
}

/* ── Footer ── */
.login-footer {
  text-align: center;
  color: #4a4c5e;
  font-size: 13px;
  margin-top: 28px;
}

.login-footer a {
  color: #d7df23;
  cursor: pointer;
  font-weight: 600;
  transition: opacity 0.15s;
}

.login-footer a:hover {
  opacity: 0.8;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .login-page {
    padding: 24px 16px;
  }

  .login-header h1 {
    font-size: 21px;
  }
}

/* mobile branding: with the DecastID hero hidden the screen was bare */
.login-logo-m {
  display: none;
}
@media (max-width: 767px) {
  .login-logo-m {
    display: block;
    width: 56px;
    height: 56px;
    margin: 4px auto 18px;
  }
  .login-header,
  .signup-header {
    text-align: center;
  }
}
</style>

<style scoped>
/* did-hero-mobile-hide: DecastID needs the browser extension (desktop only) */
@media (max-width: 767px) {
  .did-hero,
  .sep {
    display: none !important;
  }
}
</style>
